const CLOUDINARY_CLOUD_NAME = 'dh9jfogh1';

/**
 * Extracts the public ID from a full Cloudinary URL.
 * It handles URLs with or without versions and transformations.
 * @param {string} url - The full Cloudinary URL.
 * @returns {string} The extracted public ID.
 */
const extractPublicIdFromUrl = (url) => {
  try {
    // Find the '.../upload/' part of the URL, which precedes transformations and the public ID.
    const uploadMarker = '/upload/';
    const uploadIndex = url.indexOf(uploadMarker);
    
    if (uploadIndex === -1) {
      // If '/upload/' is not found, we can't reliably parse. Return the original string.
      return url;
    }

    // Get the part of the URL after '/upload/'
    let path = url.substring(uploadIndex + uploadMarker.length);

    // This part may contain transformation strings and a version number before the public ID.
    // e.g., "w_100,h_100/v12345678/folder/image.jpg"
    // We can split by '/' and filter out known transformation/version patterns.
    const pathParts = path.split('/');
    
    // Filter out version segment (e.g., v1234567890)
    // Filter out transformation segments (they often contain _ or known params like c_, w_, h_)
    // This is a heuristic: public IDs generally don't look like transformation strings.
    const publicIdParts = pathParts.filter(part => !part.match(/^v\d+$/) && !part.includes('_'));

    const publicIdWithExtension = publicIdParts.join('/');

    // Remove file extension to get the clean public ID
    const publicId = publicIdWithExtension.substring(0, publicIdWithExtension.lastIndexOf('.')) || publicIdWithExtension;

    return publicId;
  } catch (e) {
    // Fallback in case of any parsing error
    return url;
  }
};


/**
 * Generates a Cloudinary URL for an image with specified transformations.
 * @param {string} imageIdentifier - The public ID or full URL of the image.
 * @param {object} options - Transformation options.
 * @param {number} options.width - The target width of the image.
 * @param {number} options.height - The target height of the image.
 * @returns {string} The full Cloudinary URL with transformations.
 */
export const getCloudinaryUrl = (imageIdentifier, options = {}) => {
  if (!imageIdentifier) {
    return '';
  }

  let publicId = imageIdentifier;

  // Check if the identifier is a full URL and extract the public ID if so.
  if (imageIdentifier.startsWith('http')) {
    publicId = extractPublicIdFromUrl(imageIdentifier);
  } else {
    // If it's not a full URL, it might be a public ID with an extension. Remove it.
    const lastDotIndex = imageIdentifier.lastIndexOf('.');
    if (lastDotIndex !== -1) {
      publicId = imageIdentifier.substring(0, lastDotIndex);
    }
  }


  const { width = 50, height = 50 } = options;

  // Standard transformation for a small, high-quality thumbnail
  const transformations = [
    `w_${width}`,
    `h_${height}`,
    'c_thumb',      // Crop using a thumbnail algorithm
    'g_face',       // Center the crop on a detected face
    'q_auto:good',  // Adjust quality automatically to a good level
    'f_auto'        // Automatically select the best format (e.g., webp, avif)
  ].join(',');

  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/${transformations}/${publicId}`;
}; 